import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const formatDateTime = (value) => {
  if (!value) return 'Yok';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Yok';

  return date.toLocaleString('tr-TR');
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [search, setSearch] = useState('');
  const [passwordDrafts, setPasswordDrafts] = useState({});
  const [workingUserIds, setWorkingUserIds] = useState([]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getUsers();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const pendingUsers = useMemo(
    () => users.filter((user) => user.premiumRequestPending && user.role !== 'premium' && user.role !== 'admin'),
    [users]
  );

  const pendingDeletionUsers = useMemo(
    () => users.filter((user) => user.deletionRequestPending && user.role !== 'admin'),
    [users]
  );

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;

    return users.filter((user) =>
      [user.name, user.email, user.role]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [search, users]);

  const withUserAction = async (userId, action, successMessage) => {
    try {
      setError('');
      setActionMessage('');
      setWorkingUserIds((current) => [...current, userId]);
      await action();
      await loadUsers();
      setActionMessage(successMessage);
    } catch (err) {
      setError(err.message);
    } finally {
      setWorkingUserIds((current) => current.filter((id) => id !== userId));
    }
  };

  const handleGrantPremium = (userId) =>
    withUserAction(userId, () => api.grantPremium(userId, 1), 'Kullaniciya 1 aylik premium verildi.');

  const handleGrantTrialPremium = (userId) =>
    withUserAction(userId, () => api.grantPremium(userId, 0, 2), 'Kullaniciya 2 gunluk deneme premium verildi.');

  const handleRejectPremiumRequest = (userId) =>
    withUserAction(userId, () => api.rejectPremiumRequest(userId), 'Premium talebi reddedildi.');

  const handleRevokePremium = (userId) =>
    withUserAction(userId, () => api.revokePremium(userId), 'Premium uyelik iptal edildi.');

  const handleRejectDeletionRequest = (userId) =>
    withUserAction(userId, () => api.rejectAccountDeletionRequest(userId), 'Hesap silme talebi reddedildi.');

  const handleDeleteAccount = (userId) =>
    withUserAction(userId, () => api.deleteUserAccount(userId), 'Kullanici hesabi silindi.');

  const handlePasswordChange = async (userId) => {
    const newPassword = String(passwordDrafts[userId] || '').trim();

    if (newPassword.length < 6) {
      setError('Yeni sifre en az 6 karakter olmali.');
      return;
    }

    await withUserAction(
      userId,
      async () => {
        await api.adminChangeUserPassword(userId, newPassword);
        setPasswordDrafts((current) => ({ ...current, [userId]: '' }));
      },
      'Kullanici sifresi guncellendi.'
    );
  };

  return (
    <div className="page analysis-page">
      <section className="hero-panel compact user-admin-hero">
        <div className="hero-copy">
          <span className="hero-badge">Kullanici Arayuzu</span>
          <h1>Uyelik taleplerini ve premium yonetimini tek ekranda gor.</h1>
          <p>Buradan premium isteyen kullanicilari ayri olarak gorur, premium verir, isterse iptal eder ve sifrelerini degistirirsin.</p>
        </div>
      </section>

      <section className="content-panel">
        <div className="section-heading">
          <div>
            <h2>Hesap Silme Talepleri</h2>
            <p>Kullanici hesap silme talebi gonderdiyse once burada gorunur.</p>
          </div>
          <span className="section-chip">{pendingDeletionUsers.length} bekleyen silme talebi</span>
        </div>

        <div className="analysis-grid user-admin-grid">
          {pendingDeletionUsers.length === 0 ? (
            <div className="analysis-card">
              <div className="analysis-header">
                <span>Bekleyen hesap silme talebi yok</span>
                <span className="confidence medium">Temiz</span>
              </div>
              <p className="analysis-summary">Bir kullanici hesap silme talebi gonderdigi anda burada listelenecek.</p>
            </div>
          ) : (
            pendingDeletionUsers.map((user) => {
              const userId = user._id || user.id;
              const working = workingUserIds.includes(userId);

              return (
                <div key={`delete-${userId}`} className="analysis-card user-admin-card pending">
                  <div className="analysis-header">
                    <span>{user.name}</span>
                    <span className="confidence high">Silme Talebi</span>
                  </div>
                  <div className="analysis-meta">
                    <span>{user.email}</span>
                    <span>{formatDateTime(user.deletionRequestedAt)}</span>
                  </div>
                  <div className="market-row">
                    <button className="btn-primary" onClick={() => handleDeleteAccount(userId)} disabled={working}>
                      {working ? 'Isleniyor...' : 'Hesabi Sil'}
                    </button>
                    <button className="ghost-btn" onClick={() => handleRejectDeletionRequest(userId)} disabled={working}>
                      {working ? 'Isleniyor...' : 'Talebi Reddet'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="content-panel">
        <div className="section-heading">
          <div>
            <h2>Premium Talepleri</h2>
            <p>Bildirim gibi davranan alan. Kullanici premium isteyince once burada gorunur.</p>
          </div>
          <span className="section-chip">{pendingUsers.length} bekleyen talep</span>
        </div>

        {actionMessage ? <p className="analysis-summary">{actionMessage}</p> : null}
        {error ? <div className="error">{error}</div> : null}
        {loading ? <div className="loading">Kullanicilar yukleniyor...</div> : null}

        <div className="analysis-grid user-admin-grid">
          {pendingUsers.length === 0 ? (
            <div className="analysis-card">
              <div className="analysis-header">
                <span>Bekleyen premium talebi yok</span>
                <span className="confidence medium">Temiz</span>
              </div>
              <p className="analysis-summary">Yeni bir kullanici premium talebi gonderdigi anda burada listelenecek.</p>
            </div>
          ) : (
            pendingUsers.map((user) => {
              const userId = user._id || user.id;
              const working = workingUserIds.includes(userId);

              return (
                <div key={`pending-${userId}`} className="analysis-card user-admin-card pending">
                  <div className="analysis-header">
                    <span>{user.name}</span>
                    <span className="confidence high">Talep Var</span>
                  </div>
                  <div className="analysis-meta">
                    <span>{user.email}</span>
                    <span>{formatDateTime(user.premiumRequestedAt)}</span>
                  </div>
                  <div className="market-row">
                    <button className="btn-primary" onClick={() => handleGrantPremium(userId)} disabled={working}>
                      {working ? 'Isleniyor...' : '1 Aylik Premium Ver'}
                    </button>
                    <button className="ghost-btn" onClick={() => handleGrantTrialPremium(userId)} disabled={working}>
                      {working ? 'Isleniyor...' : '2 Gun Deneme Ver'}
                    </button>
                    <button className="ghost-btn" onClick={() => handleRejectPremiumRequest(userId)} disabled={working}>
                      {working ? 'Isleniyor...' : 'Hayir'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="content-panel">
        <div className="section-heading">
          <div>
            <h2>Tum Kullanicilar</h2>
            <p>Ayrik kullanici arayuzu. Premium, iptal ve sifre islerini sadece buradan yonetebilirsin.</p>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="user-search">Kullanici ara</label>
          <input
            id="user-search"
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Isim, e-posta veya rol"
          />
        </div>

        <div className="analysis-grid user-admin-grid">
          {filteredUsers.map((user) => {
            const userId = user._id || user.id;
            const working = workingUserIds.includes(userId);
            const isAdminUser = user.role === 'admin';
            const isPremiumUser = user.role === 'premium';

            return (
              <div key={userId} className={`analysis-card user-admin-card ${user.premiumRequestPending ? 'pending' : ''}`}>
                <div className="analysis-header">
                  <span>{user.name}</span>
                  <span className={`confidence ${isPremiumUser ? 'high' : 'medium'}`}>{user.role}</span>
                </div>
                <div className="analysis-meta">
                  <span>{user.email}</span>
                  <span>Kayit: {formatDateTime(user.createdAt)}</span>
                </div>
                <div className="user-admin-meta-list">
                  <span>Premium bitis: {formatDateTime(user.premiumExpiresAt)}</span>
                  <span>Talep tarihi: {formatDateTime(user.premiumRequestedAt)}</span>
                  <span>Silme talebi: {formatDateTime(user.deletionRequestedAt)}</span>
                </div>
                <div className="market-row">
                  <button
                    className="btn-primary"
                    onClick={() => handleGrantPremium(userId)}
                    disabled={working || isAdminUser}
                  >
                    {working ? 'Isleniyor...' : 'Premium Ver'}
                  </button>
                  <button
                    className="ghost-btn"
                    onClick={() => handleGrantTrialPremium(userId)}
                    disabled={working || isAdminUser}
                  >
                    2 Gun Deneme
                  </button>
                  <button
                    className="ghost-btn"
                    onClick={() => handleRevokePremium(userId)}
                    disabled={working || isAdminUser || !isPremiumUser}
                  >
                    Uyelik Iptal
                  </button>
                  <button
                    className="ghost-btn"
                    onClick={() => handleRejectPremiumRequest(userId)}
                    disabled={working || isAdminUser || !user.premiumRequestPending}
                  >
                    Hayir
                  </button>
                  <button
                    className="ghost-btn"
                    onClick={() => handleRejectDeletionRequest(userId)}
                    disabled={working || isAdminUser || !user.deletionRequestPending}
                  >
                    Silme Talebini Kapat
                  </button>
                </div>
                <div className="form-group user-admin-password-group">
                  <label htmlFor={`password-${userId}`}>Yeni sifre</label>
                  <input
                    id={`password-${userId}`}
                    type="text"
                    value={passwordDrafts[userId] || ''}
                    onChange={(event) =>
                      setPasswordDrafts((current) => ({ ...current, [userId]: event.target.value }))
                    }
                    placeholder="En az 6 karakter"
                  />
                </div>
                <button
                  className="ghost-btn"
                  onClick={() => handlePasswordChange(userId)}
                  disabled={working || isAdminUser}
                >
                  Sifreyi Degistir
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default AdminUsers;
